package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.identity.Codec

/**
 * One atomic replicated-event transport unit.
 *
 * [payloadLines] contains only `OCT` / `INT` / `LNK` / `CMT` / `SEAL` lines. [wireLines] wraps them in the mandatory
 * batch-v1 envelope. The digest authenticates the exact UTF-8 payload joined with `\n`, without a trailing newline.
 * This is a transport projection: the durable facts remain the expanded [events].
 */
data class ReplicatedEventBatch(
    val payloadLines: List<String>,
    val events: List<ReplicatedEvent>,
    val digest: String,
    val wireBytes: Int,
) {
    val expandedEvents: Int get() = events.size
    val uniqueEvents: Int get() = events.distinctBy(::eventIdentity).size

    /** Complete batch-v1 wire form, including its authenticated envelope. */
    fun wireLines(): List<String> = batchWireLines(payloadLines, wireBytes, expandedEvents, digest)
}

/**
 * Frame a payload in the batch-v1 envelope from the four header facts, without needing the events themselves.
 *
 * A store that kept the accepted wire has all four and nothing to gain from decoding it: re-framing is string work,
 * while decoding regenerates every recipe closure in the payload and projects it, only to throw the result away. One
 * definition because there are now two callers — a decoded batch and an archived one — and a wire form that two places
 * spell out is a wire form that can disagree with itself.
 */
fun batchWireLines(payloadLines: List<String>, wireBytes: Int, expandedEvents: Int, digest: String): List<String> =
    buildList(payloadLines.size + 2) {
        add("BATCH|1|${payloadLines.size}|$wireBytes|$expandedEvents|$digest")
        addAll(payloadLines)
        add("END|$digest")
    }

/** Mandatory batch-v1 envelope around the existing compact recipe payload. */
class BatchWireCodec(private val projector: LinkProjector) {
    private val recipes = RecipeWireCodec(projector)

    /**
     * Encode events into one or more bounded batches. Dependency-only batches precede a batch containing the CMT when
     * the compact payload crosses a limit; no batch ever contains more than one signed commitment.
     *
     * Batching needs to know what each line expands to — that is what the cardinality limits are counted over — and
     * [RecipeWireCodec.encodeLines] hands that over as part of encoding. It used to be recovered instead by decoding
     * the codec's own output: once per line to count it, then once more over the whole payload to build the batch. On a
     * full-state export of a real store that double decode was about a third of the encode, and it bought nothing that
     * the loop below does not already check — every limit [decodePayload] re-verifies is enforced here before a line is
     * admitted. What it *did* buy was the guarantee that a batch's events are what a receiver would decode; that is now
     * a property test over the codec ([RecipeWireCodec.encodeLines]) rather than a per-export cost.
     */
    fun encode(events: Iterable<ReplicatedEvent>, resolver: ProjectedLinkRecordResolver): List<ReplicatedEventBatch> {
        val payload = recipes.encodeLines(events, resolver)
        if (payload.isEmpty()) return emptyList()
        val batches = mutableListOf<ReplicatedEventBatch>()
        val current = mutableListOf<String>()
        val currentEvents = mutableListOf<ReplicatedEvent>()
        var currentBytes = 0
        var currentCommitments = 0
        var currentSeals = 0

        fun flush() {
            if (current.isEmpty()) return
            // `currentBytes` counts each line plus one separator byte between them, which is exactly the length of the
            // payload joined with "\n" — the same bytes [digest] and the receiver's [decodePayload] measure.
            batches += ReplicatedEventBatch(current.toList(), currentEvents.toList(), digest(current), currentBytes)
            current.clear()
            currentEvents.clear()
            currentBytes = 0
            currentCommitments = 0
            currentSeals = 0
        }

        for ((line, expanded) in payload) {
            require(expanded.isNotEmpty() && isDataLine(line)) { "Cannot batch an invalid compact payload line." }
            val lineBytes = line.encodeToByteArray().size
            val separatorBytes = if (current.isEmpty()) 0 else 1
            val commitments = expanded.count { it is SignedCommitment }
            val seals = expanded.count { it is SealedContent }
            require(commitments <= MAX_COMMITMENTS && seals <= MAX_SEALS)
                { "One compact payload line exceeds a batch cardinality limit." }
            val exceeds = current.size + 1 > MAX_PAYLOAD_LINES ||
                currentBytes + separatorBytes + lineBytes > MAX_PAYLOAD_BYTES ||
                currentEvents.size + expanded.size > MAX_EXPANDED_EVENTS ||
                currentCommitments + commitments > MAX_COMMITMENTS ||
                currentSeals + seals > MAX_SEALS
            if (exceeds) flush()
            require(lineBytes <= ReplicatedEventWireLimits.MAX_LINE_BYTES && lineBytes <= MAX_PAYLOAD_BYTES &&
                expanded.size <= MAX_EXPANDED_EVENTS)
                { "One compact payload line exceeds a batch resource limit." }
            currentBytes += if (current.isEmpty()) lineBytes else lineBytes + 1
            current += line
            currentEvents += expanded
            currentCommitments += commitments
            currentSeals += seals
        }
        flush()
        return batches
    }

    /** Decode and validate an already-delimited payload. */
    fun decodePayload(payloadLines: List<String>): ReplicatedEventBatch? {
        if (payloadLines.isEmpty() || payloadLines.size > MAX_PAYLOAD_LINES) return null
        if (payloadLines.any { ! isDataLine(it) || it.encodeToByteArray().size > ReplicatedEventWireLimits.MAX_LINE_BYTES })
            return null
        val payload = payloadLines.joinToString("\n")
        val wireBytes = payload.encodeToByteArray().size
        if (wireBytes > MAX_PAYLOAD_BYTES) return null
        val events = mutableListOf<ReplicatedEvent>()
        for (line in payloadLines) {
            val decoded = recipes.decodeLine(line)
            if (decoded.isEmpty() || events.size + decoded.size > MAX_EXPANDED_EVENTS) return null
            events += decoded
        }
        if (events.count { it is SignedCommitment } > MAX_COMMITMENTS || events.count { it is SealedContent } > MAX_SEALS)
            return null
        return ReplicatedEventBatch(payloadLines, events, digest(payloadLines), wireBytes)
    }

    /** Per-session parser. A stager is intentionally process-local; hibernating hosts must discard incomplete state. */
    inner class Stager {
        private var pending: Pending? = null

        val isStaging: Boolean get() = pending != null

        fun accept(line: String): StageResult = accept(line, batchClockMillis())

        fun accept(line: String, nowMillis: Long): StageResult {
            val active = pending
            if (active != null && nowMillis - active.startedAtMillis > STAGING_TTL_MILLIS) {
                pending = null
                return StageResult.Violation("batch staging timed out")
            }
            if (line.startsWith(BATCH_PREFIX)) {
                if (active != null) {
                    pending = null
                    return StageResult.Violation("nested batch")
                }
                val header = parseHeader(line) ?: return StageResult.Violation("malformed batch header")
                pending = Pending(header, nowMillis, mutableListOf())
                return StageResult.Pending
            }
            if (line.startsWith(END_PREFIX)) {
                val staged = active ?: return StageResult.Violation("batch END without BATCH")
                pending = null
                val end = line.split(FIELD_SEPARATOR)
                if (end.size != 2 || end[1] != staged.header.digest)
                    return StageResult.Violation("batch END digest mismatch")
                val batch = decodePayload(staged.lines) ?: return StageResult.Violation("invalid batch payload")
                val header = staged.header
                if (batch.payloadLines.size != header.lineCount || batch.wireBytes != header.wireBytes ||
                    batch.expandedEvents != header.expandedEvents || batch.digest != header.digest)
                    return StageResult.Violation("batch count or digest mismatch")
                return StageResult.Complete(batch)
            }
            if (active == null) return StageResult.NotBatch
            if (! isDataLine(line)) {
                pending = null
                return StageResult.Violation("control or unknown line inside batch")
            }
            active.lines += line
            if (active.lines.size > active.header.lineCount || active.lines.size > MAX_PAYLOAD_LINES) {
                pending = null
                return StageResult.Violation("batch payload line limit exceeded")
            }
            return StageResult.Pending
        }

        fun discard() { pending = null }
    }

    sealed interface StageResult {
        data object NotBatch : StageResult
        data object Pending : StageResult
        data class Complete(val batch: ReplicatedEventBatch) : StageResult
        data class Violation(val reason: String) : StageResult
    }

    private data class Header(val lineCount: Int, val wireBytes: Int, val expandedEvents: Int, val digest: String)
    private data class Pending(val header: Header, val startedAtMillis: Long, val lines: MutableList<String>)

    private fun parseHeader(line: String): Header? = runCatching {
        val parts = line.split(FIELD_SEPARATOR)
        if (parts.size != 6 || parts[0] != BATCH_TAG || parts[1] != VERSION) return null
        val lineCount = parts[2].toInt()
        val wireBytes = parts[3].toInt()
        val expandedEvents = parts[4].toInt()
        val digest = parts[5]
        if (lineCount !in 1..MAX_PAYLOAD_LINES || wireBytes !in 1..MAX_PAYLOAD_BYTES ||
            expandedEvents !in 1..MAX_EXPANDED_EVENTS || digest.isEmpty()) return null
        Header(lineCount, wireBytes, expandedEvents, digest)
    }.getOrNull()

    companion object {
        const val MAX_PAYLOAD_LINES = 128
        /** Maximum complete batch-v1 capsule size; every peer transport must accept this many exact bytes. */
        const val MAX_WIRE_BYTES = 786_234

        // The largest possible batch envelope is below 128 bytes with the bounded fields below.
        const val MAX_PAYLOAD_BYTES = MAX_WIRE_BYTES - 128
        const val MAX_EXPANDED_EVENTS = 4096
        const val MAX_COMMITMENTS = 1
        const val MAX_SEALS = 16
        const val STAGING_TTL_MILLIS = 30_000L

        private const val BATCH_TAG = "BATCH"
        private const val VERSION = "1"
        private const val BATCH_PREFIX = "$BATCH_TAG|"
        private const val END_PREFIX = "END|"
        private const val FIELD_SEPARATOR = '|'
        private val DATA_TAGS = setOf("OCT", "INT", "LNK", "CMT", "SEAL")

        fun isBatchBoundary(line: String): Boolean = line.startsWith(BATCH_PREFIX) || line.startsWith(END_PREFIX)
        fun isDataLine(line: String): Boolean = line.substringBefore(FIELD_SEPARATOR) in DATA_TAGS
        fun isBatchLine(line: String): Boolean = isBatchBoundary(line) || isDataLine(line)

        /** Digest of the exact UTF-8 payload joined by `\n`, without a trailing newline. */
        fun digest(payloadLines: List<String>): String =
            Codec.base64Url(Codec.sha256(payloadLines.joinToString("\n").encodeToByteArray()))
    }
}

private fun eventIdentity(event: ReplicatedEvent): Any = when (event) {
    is LinkPreimage -> "L:${event.projection.payload}"
    is SignedCommitment -> "C:${event.head.payload}"
    is SealedContent -> "S:${event.contentProjection.payload}:${event.ciphertextDigest}"
}
